import { urlConfig } from '@/config';
import {
  clearLastSignedInEmail,
  getLastSignedInEmail,
} from '@/contexts/auth-context';
import { SAN_FRANCISCO_HUB_PATH_IDENTIFIER } from '@/lib/constants';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';

// The backend starts the Google OAuth redirect and owns the CSRF state cookie.
// Stash the post-sign-in target in sessionStorage on the frontend origin so
// /auth/complete can restore the user's original destination after the backend
// redirects back with our session token.
export const REDIRECT_AFTER_SIGNIN_KEY = 'paddock.auth.redirect_after_signin';

interface AuthSearch {
  redirect?: string;
  error?: string;
  email?: string;
}

export const Route = createFileRoute('/auth/')({
  validateSearch: (search: Record<string, unknown>): AuthSearch => ({
    redirect: search.redirect as string | undefined,
    error: search.error as string | undefined,
    email: search.email as string | undefined,
  }),
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({
        to: '/$hub/all',
        params: {
          hub:
            context.auth.user?.current_hub ?? SAN_FRANCISCO_HUB_PATH_IDENTIFIER,
        },
      });
    }
  },
  component: SignIn,
});

function SignIn() {
  const search = Route.useSearch();
  const [lastEmail, setLastEmail] = useState(() => getLastSignedInEmail());
  const googleStartUrl = new URL(`${urlConfig.authUrl}/google-start`);
  googleStartUrl.searchParams.set('frontend_url', window.location.origin);
  const email = search.email?.trim() || lastEmail;
  const hasEmailHint = !!email;

  const handleUseDifferentEmail = () => {
    clearLastSignedInEmail();
    setLastEmail(null);
  };

  useEffect(() => {
    if (search.redirect) {
      sessionStorage.setItem(REDIRECT_AFTER_SIGNIN_KEY, search.redirect);
    } else {
      sessionStorage.removeItem(REDIRECT_AFTER_SIGNIN_KEY);
    }
  }, [search.redirect]);

  return (
    <div className="grid min-h-screen bg-white text-[#171717] dark:bg-white dark:text-[#171717] lg:grid-cols-2">
      <section className="flex min-h-screen flex-col px-8 py-10 sm:px-16">
        <div className="text-2xl font-semibold tracking-tight">Paddock</div>

        <main className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-[680px]">
            <h1 className="mb-7 text-[34px] font-normal leading-none tracking-normal">
              {hasEmailHint ? 'Welcome back!' : 'Welcome!'}
            </h1>

            {hasEmailHint ? (
              <>
                <div className="mb-6 flex h-[66px] items-center justify-between border border-[#cfc9c5] px-4">
                  <div>
                    <div className="mb-2 text-sm leading-none text-[#9d9895]">
                      Email address*
                    </div>
                    <div className="text-lg leading-none text-[#9d9895]">
                      {email}
                    </div>
                  </div>
                  <Lock className="size-5 text-[#9d9895]" strokeWidth={1.8} />
                </div>

                <div className="mb-[70px] flex flex-col gap-4 text-base text-[#5f5a57] sm:flex-row sm:items-center sm:justify-between">
                  <a
                    href="/auth"
                    onClick={handleUseDifferentEmail}
                    className="inline-flex items-center gap-3 underline underline-offset-4"
                  >
                    <ArrowLeft className="size-4" strokeWidth={1.8} />
                    <span>Use a different email</span>
                  </a>
                  <a
                    href="mailto:support@evercars.com"
                    className="inline-flex items-center gap-1 underline underline-offset-4"
                  >
                    <span>Trouble signing in?</span>
                    <ExternalLink className="size-4" strokeWidth={1.8} />
                  </a>
                </div>
              </>
            ) : (
              <div className="mb-[70px]">
                <p className="mb-4 max-w-[430px] text-base leading-7 text-[#5f5a57]">
                  Sign in with your Ever Google account to continue to Paddock.
                </p>
                <a
                  href="mailto:support@evercars.com"
                  className="inline-flex items-center gap-1 text-base text-[#5f5a57] underline underline-offset-4"
                >
                  <span>Trouble signing in?</span>
                  <ExternalLink className="size-4" strokeWidth={1.8} />
                </a>
              </div>
            )}

            {search.error && (
              <div className="mb-5 text-sm text-red-600">
                Sign-in failed. Please try again.
              </div>
            )}

            <a
              href={googleStartUrl.toString()}
              className="flex h-[66px] w-full items-center justify-center border border-[#cfc9c5] bg-white px-4 text-xl font-normal text-[#1f1f1f] transition-colors hover:bg-[#f3f1ef] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#1f1f1f] focus-visible:ring-offset-2"
            >
              <span
                className="mr-4 flex size-[18px] items-center justify-center"
                aria-hidden="true"
              >
                <GoogleIcon />
              </span>
              <span>Sign in with Google</span>
            </a>
          </div>
        </main>
      </section>

      <aside className="hidden min-h-screen bg-[#dfe8f6] lg:flex lg:items-center lg:justify-center">
        <div className="max-w-[520px] px-10 text-center">
          <h2 className="mb-8 text-[34px] font-medium leading-tight">
            Built for focused deal work.
          </h2>
          <p className="text-lg leading-8">
            Paddock keeps your pipeline, customers, and vehicle workflow in one
            quiet workspace.
          </p>
        </div>
      </aside>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 18 18" className="size-[18px]">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.35 0-4.34-1.58-5.05-3.71H.93v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.71A5.41 5.41 0 0 1 3.67 9c0-.59.1-1.16.28-1.71V4.96H.93A9 9 0 0 0 0 9c0 1.45.34 2.82.93 4.04l3.02-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .93 4.96l3.02 2.33C4.66 5.16 6.65 3.58 9 3.58z"
      />
    </svg>
  );
}
